import { useCreateRegularAssessment } from "@/api/console/regular-assessments/useCreateRegularAssessment";
import { useGetLatestRegularAssessment } from "@/api/console/regular-assessments/useGetLatestRegularAssessment";
import { useGetSdgTargets } from "@/api/console/sdg-targets/useGetSdgTargets";
import Category from "@/components/Assessments/Category";
import CategoryPreview from "@/components/Assessments/CategoryPreview";
import ReorderCategoryDialog from "@/components/Assessments/ReorderCategoryDialog";
import LoadingScreen from "@/components/Loading";
import { Button } from "@/components/ui/button";
import useSdgOptions from "@/hooks/useSdgOptions";
import toast from "@/lib/toast";
import { trimString } from "@/lib/trimString";
import { Category as CategoryFormValues } from "@/schema/console/assessments/create/Category";
import {
  FormValues,
  formValuesSchema,
} from "@/schema/console/assessments/create/FormValues";
import { Question as QuestionFormValues } from "@/schema/console/assessments/create/Question";
import { SubCategory as SubCategoryFormValues } from "@/schema/console/assessments/create/SubCategory";
import { MainCategoryCreate } from "@/schema/console/regular-assessments/MainCategory";
import { QuestionCreate } from "@/schema/console/regular-assessments/Question";
import { RegularAssessmentCreate } from "@/schema/console/regular-assessments/RegularAssessmentDetail";
import { SubCategoryCreate } from "@/schema/console/regular-assessments/SubCategory";
import Heading from "@/src/console/(auth)/(manage-assessments)/regular-assessments/create/_components/Heading";
import { AddRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { v1 as uuid } from "uuid";

function CreateRegularAssessment() {
  const { t } = useTranslation();
  const method = useForm<FormValues>({
    resolver: zodResolver(formValuesSchema),
    defaultValues: {
      selectedCategory: "",
      categories: [],
    },
  });
  const { control, handleSubmit } = method;

  const [searchParams] = useSearchParams();
  const duplicate = searchParams.get("duplicate") === "true";

  const { setSdgOptions } = useSdgOptions();

  const { mutate, isLoading } = useGetLatestRegularAssessment({
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: false,
    revalidateOnReconnect: false,
    onSuccess: (data) => {
      const categories = data.data.main_categories.map(
        ({ sub_categories: sc, content }) => {
          const subcategories = sc.map(({ questions: q, content }) => {
            const questions = q.map(
              ({ content, points, evaluations: e, sdg_target_ids }) => {
                const evaluations = e.map(({ content, weight }) => {
                  return {
                    id: uuid(),
                    content,
                    weight,
                  };
                });
                return {
                  content,
                  id: uuid(),
                  type: "regular" as const,
                  isEdit: false,
                  perfectScore: points,
                  evaluations,
                  sdgs: sdg_target_ids,
                };
              },
            );
            return {
              id: uuid(),
              content,
              isEdit: false,
              questions,
            };
          });
          return {
            id: uuid(),
            content,
            isEdit: false,
            selectedSubCategory: subcategories[subcategories.length - 1].id,
            subcategories,
          };
        },
      );
      method.reset({
        selectedCategory: categories
          ? categories[categories.length - 1].id
          : "",
        categories,
      });
    },
  });

  const { isLoading: isSdgLoading } = useGetSdgTargets({
    onSuccess: (data) => {
      setSdgOptions(
        data.data.map(({ id, target }) => ({
          label: trimString(target, 100),
          value: id,
        })),
      );
    },
  });

  const { trigger: saveDraft, isMutating: isSavingDraft } =
    useCreateRegularAssessment();

  const { trigger: publish, isMutating: isPublishing } =
    useCreateRegularAssessment();

  useEffect(() => {
    if (!duplicate) return;
    mutate();
  }, [duplicate]);

  const {
    fields: categoryFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "categories",
    keyName: "key",
  });

  const selectedCategory = useWatch({
    control,
    name: "selectedCategory",
  });

  const prepareResponseQuestions = (
    questions: QuestionFormValues[],
  ): QuestionCreate[] => {
    return questions.map(({ content, perfectScore, ...question }) => {
      return {
        content,
        evaluations: question.type === "regular" ? question.evaluations : [],
        points: perfectScore,
        sdg_target_ids: question.type === "regular" ? question.sdgs : [],
      };
    });
  };

  const prepareResponseSubcategories = (
    subcategories: SubCategoryFormValues[],
  ): SubCategoryCreate[] => {
    return subcategories.map(({ content, questions }) => {
      return {
        content,
        questions: prepareResponseQuestions(questions),
      };
    });
  };

  const prepareResponseCategories = (
    categories: CategoryFormValues[],
  ): MainCategoryCreate[] => {
    return categories.map(({ content, subcategories }) => {
      return {
        content,
        sub_categories: prepareResponseSubcategories(subcategories),
      };
    });
  };

  const prepareResponseData = (
    data: FormValues,
    isPublished: boolean,
  ): RegularAssessmentCreate => {
    return {
      is_premium: true,
      is_published: isPublished,
      main_categories: prepareResponseCategories(data.categories),
    };
  };

  const onSaveDraft = () => {
    handleSubmit(
      (data) => {
        toast.loading(t("save-draft-loading"));
        saveDraft(prepareResponseData(data, false));
      },
      (errors) => {
        if (errors) {
          toast.error(JSON.stringify(errors));
        }
      },
    )();
  };

  const onPublish = () => {
    handleSubmit(
      (data) => {
        toast.loading(t("publish-loading"));
        publish(prepareResponseData(data, true));
      },
      (errors) => {
        if (errors) {
          toast.error(JSON.stringify(errors));
        }
      },
    )();
  };

  if (isLoading || isSdgLoading) {
    return <LoadingScreen />;
  }
  return (
    <FormProvider {...method}>
      <Heading
        isPublishing={isPublishing}
        isSavingDraft={isSavingDraft}
        onSaveDraft={onSaveDraft}
        onPublish={onPublish}
      />
      <div className="flex min-h-[calc(100%-168px)]">
        {/* Category list */}
        <div className="w-[300px] min-w-[300px] border-r bg-white p-4">
          <div className="mb-2 ml-3 flex items-start justify-between gap-1">
            <div className="text-lg font-bold">{t("categories")}</div>
            <div className="flex gap-2">
              <ReorderCategoryDialog
                onReorderFinish={(categories) => {
                  replace(categories);
                }}
              />
              <Button
                onClick={() => {
                  append({
                    id: uuid(),
                    content: "",
                    isEdit: true,
                    selectedSubCategory: "",
                    subcategories: [],
                  });
                }}
                variant="default"
                size="icon"
              >
                <AddRegular fontSize={20} />
              </Button>
            </div>
          </div>
          {categoryFields.map((category, categoryIndex) => (
            <CategoryPreview
              key={category.id}
              index={categoryIndex}
              onRemove={() => {
                remove(categoryIndex);
              }}
            />
          ))}
        </div>
        <div className="grow">
          {categoryFields.map((category, index) => {
            return (
              category.id === selectedCategory && (
                <Category type="regular" key={category.id} index={index} />
              )
            );
          })}
        </div>
      </div>
    </FormProvider>
  );
}

export default CreateRegularAssessment;
